import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { AppModuleLayout } from "@layouts";
import { PageHead, SectionBlockRenderer, ClipLoaderCommon } from "@components";
import { cmsApi } from "@api";
import { CmsPage as ICmsPage } from "@interfaces";
import { theme, getSiteMeta, buildFaqJsonLd, buildBreadcrumbJsonLd, stripHtml } from "@utilities";
import { NotFound } from "../NotFound";

/** Collect FAQ question/answer pairs from a page's `faq` sections for JSON-LD. */
const collectFaq = (page: ICmsPage) =>
    (page.sections || [])
        .filter((s) => s.type === "faq")
        .flatMap((s) => s.data.items || [])
        .map((item) => ({
            question: item.question || item.title || "",
            answer: stripHtml(item.answer || item.body || ""),
        }))
        .filter((f) => f.question && f.answer);

type LoadState = "loading" | "ready" | "notfound" | "error";

/**
 * Renders a backend-managed standalone page at a clean slug (`/:pageSlug`).
 * `?preview=1` requests draft content (backend enforces auth for drafts).
 * Shows loading, not-found (unpublished/missing) and error states.
 */
export const CmsPage = () => {
    const { pageSlug } = useParams<{ pageSlug: string }>();
    const [searchParams] = useSearchParams();
    const preview = searchParams.get("preview") === "1" || searchParams.get("preview") === "true";
    const [state, setState] = useState<LoadState>("loading");
    const [page, setPage] = useState<ICmsPage | null>(null);

    // Deterministic fallback title on mount so the tab/analytics never show the
    // previous route's title while the page is still loading. PageHead replaces
    // it with the real page title once the fetch resolves.
    useEffect(() => {
        document.title = theme.title;
    }, [pageSlug]);

    useEffect(() => {
        let active = true;
        setState("loading");
        window.scrollTo({ top: 0 });
        (async () => {
            try {
                const result = await cmsApi.getPageBySlug(pageSlug ?? "", { preview });
                if (!active) return;
                if (!result) {
                    setState("notfound");
                    return;
                }
                setPage(result);
                setState("ready");
            } catch (err) {
                if (!active) return;
                console.error("[CmsPage] failed to load", err);
                setState("error");
            }
        })();
        return () => {
            active = false;
        };
    }, [pageSlug, preview]);

    if (state === "notfound") return <NotFound />;

    return (
        <AppModuleLayout>
            <Container>
                {state === "loading" && (
                    <ClipLoaderCommon color={theme.colors.highlight} size={60} margin={120} />
                )}
                {state === "error" && (
                    <div className="cmsMessage">
                        <h2>Something went wrong</h2>
                        <p>We couldn’t load this page. Please try again shortly.</p>
                    </div>
                )}
                {state === "ready" && page && (
                    <>
                        <PageHead
                            seo={page.seo}
                            jsonLd={(() => {
                                const site = getSiteMeta();
                                const path = `/${pageSlug ?? ""}`;
                                const url = site.siteUrl ? `${site.siteUrl}${path}` : path;
                                const nodes = [
                                    buildBreadcrumbJsonLd(
                                        [
                                            { name: "Home", url: site.siteUrl || "/" },
                                            { name: page.title || page.seo?.title || "Page", url },
                                        ],
                                        site,
                                    ),
                                    buildFaqJsonLd(collectFaq(page)),
                                ].filter(Boolean);
                                return nodes;
                            })()}
                        />
                        {preview && page.status !== "published" && (
                            <PreviewBanner>Preview — this content is not published yet.</PreviewBanner>
                        )}
                        <SectionBlockRenderer sections={page.sections} />
                    </>
                )}
            </Container>
        </AppModuleLayout>
    );
};

const Container = styled.div`
    background-color: #fff;
    width: 100%;
    min-height: 60vh;
    padding: 24px 0 60px;

    .cmsMessage {
        text-align: center;
        padding: 80px 16px;
    }
`;

const PreviewBanner = styled.div`
    background: #fff4d6;
    color: #7a5b00;
    text-align: center;
    padding: 10px 16px;
    font-weight: 600;
    margin-bottom: 16px;
`;
